/** @format */

// src/context/AuthContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import {
  checkAuthStatus as apiCheckAuthStatus,
  login as apiLogin,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
  /*   verifyUser as apiVerifyUser, // Import the new API method */
  refreshUserInfo as apiRefreshUserInfo, // Import the new refresh user info method
} from './fetch_api'; // Import the new API methods

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the token refresh function
  const refreshToken = useCallback(async () => {
    try {
      await apiRefreshToken();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await apiCheckAuthStatus();
      return response;
    } catch (error) {
      console.error('Auth check failed:', error);
      return false;
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiLogin({ email, password });
      setIsAuthenticated(true);
      setUser(response.user);
      sessionStorage.setItem('user', JSON.stringify(response.user));
      sessionStorage.setItem(
        'exploracoes',
        JSON.stringify(response.exploracoes)
      );

      // In your login function or where you handle the login response
      // console.log('Cookies after login:', document.cookie);
      // console.log(
      //   'All cookies:',
      //   Object.fromEntries(
      //     document.cookie.split('; ').map((cookie) => cookie.split('='))
      //   )
      // );

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('exploracoes');
    }
  }, []);

  const refreshUserData = useCallback(async () => {
    try {
      // Use the new refreshUserInfo endpoint to get fresh user data
      const response = await apiRefreshUserInfo();
      if (response && response.user) {
        setUser(response.user);
        sessionStorage.setItem('user', JSON.stringify(response.user));
        return response.user;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  }, []);

  //
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        const response = await checkAuthStatus();
        if (response.isAuthenticated) {
          setIsAuthenticated((prev) => {
            if (!prev) return true; // Only update if the state changes
            return prev;
          });
          setUser((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(response.user)) {
              return response.user; // Update only if the user data changes
            }
            return prev;
          });
          sessionStorage.setItem('user', JSON.stringify(response.user));
        } else {
          setIsAuthenticated(false);
          setUser(null);
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('exploracoes');
        }
      }
      setIsLoading(false);
    };
    initializeAuth();
  }, [checkAuthStatus]);

  // Define redirectToLogin outside of useEffect
  const redirectToLogin = useCallback(() => {
    window.location.href = '/login'; // Redirect to the login page
  }, []);

  // Combine token refresh and inactivity timeout in one effect
  useEffect(() => {
    let refreshInterval;
    let inactivityTimeout;

    const resetInactivityTimeout = () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(() => {
        logout();
        redirectToLogin();
      }, 30 * 60 * 1000); // 30 minutes
    };

    if (isAuthenticated) {
      // Set up token refresh
      refreshInterval = setInterval(async () => {
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          logout();
          redirectToLogin();
        }
      }, 15 * 60 * 1000); // 15 minutes

      // Set up inactivity monitoring
      resetInactivityTimeout();
      window.addEventListener('mousemove', resetInactivityTimeout);
      window.addEventListener('keydown', resetInactivityTimeout);
    }

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      window.removeEventListener('mousemove', resetInactivityTimeout);
      window.removeEventListener('keydown', resetInactivityTimeout);
    };
  }, [isAuthenticated, refreshToken, logout, redirectToLogin]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        refreshUserData,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
